import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth";
import LogoLogin from "../components/LogoLogin";
import AuthStage from "../components/auth/AuthStage";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";

export default function Register() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  // animation-only state: shake on invalid submit, slide-out on success
  const [shake, setShake] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect logged-in users to their dashboard
  useEffect(() => {
    if (user) {
      const normalizedRole = user?.role?.toUpperCase();
      const roleRoutes = {
        COACH: "/coachdashboard",
        ADMIN: "/admin",
        OWNER: "/admin",
        PARENT: "/dashboard",
        STUDENT: "/dashboard",
      };
      const targetRoute = roleRoutes[normalizedRole] || "/dashboard";
      navigate(targetRoute, { replace: true });
    }
  }, [user, navigate]);

  const redirectForRole = (user) => {
    const normalizedRole = user?.role?.toUpperCase();
    const roleMapping = {
      COACH: { route: "/coachdashboard", storage: "coach" },
      ADMIN: { route: "/admin", storage: "admin" },
      OWNER: { route: "/admin", storage: "admin" },
      PARENT: { route: "/dashboard", storage: "parent" },
      STUDENT: { route: "/dashboard", storage: "student" },
    };

    const target = normalizedRole ? roleMapping[normalizedRole] : undefined;

    if (target) {
      localStorage.setItem("role", target.storage);

      // Priority 1: Check if user was trying to register for a class
      const intendedClass = sessionStorage.getItem("intendedClass");
      if (intendedClass) {
        sessionStorage.removeItem("intendedClass");

        // Only allow parents to access checkout
        if (normalizedRole === "PARENT") {
          navigate(`/checkout?classId=${intendedClass}`, { replace: true });
          return;
        } else {
          // Staff land on their own dashboard: they enrol a child from the
          // class page, which opens the admin form rather than parent
          // checkout. Checkout would bill them instead of the family.
          toast(
            "Open the class and use Register there to enrol a child for a family.",
          );
          navigate(target.route, { replace: true });
          return;
        }
      }

      // Priority 2: Check if user was redirected from another page (deep link).
      const fromLocation = location.state?.from;
      const from = fromLocation
        ? fromLocation.pathname + (fromLocation.search || "")
        : null;
      // Never "return" a user to ANOTHER role's home dashboard via `from`
      // (deep sub-links are still honored; role homes are reached via role logic).
      const ROLE_HOME_ROUTES = ["/dashboard", "/coachdashboard", "/admin"];
      const fromPath = from ? from.split("?")[0] : null;
      const isForeignHome =
        fromPath &&
        ROLE_HOME_ROUTES.includes(fromPath) &&
        fromPath !== target.route;
      const intendedRoute =
        from && from !== "/register" && !isForeignHome ? from : target.route;

      // Navigate to intended page or fallback to role-based default
      navigate(intendedRoute, { replace: true });
    } else {
      console.warn("Unknown role:", user?.role);
      localStorage.setItem("role", "parent");
      navigate("/dashboard", { replace: true });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    // Confirm password validation
    if (!formData.confirm_password) {
      newErrors.confirm_password = "Please confirm your password";
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    // Phone validation (required)
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = "Invalid phone number format";
    }

    // Address validation (required)
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    // City validation (required)
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // State validation (required)
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    // Zip code validation (required)
    if (!formData.zip_code.trim()) {
      newErrors.zip_code = "Zip code is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      setShake(Date.now());
      setTimeout(() => setShake(0), 340);
      return;
    }

    setLoading(true);

    try {
      const user = await register(formData);
      toast.success("Account created successfully!");
      // brief slide-out before navigating (animation only)
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 380));
      redirectForRole(user);
    } catch (error) {
      // Error already handled by auth context (toast shown)
      console.error("Registration error:", error);
      setShake(Date.now());
      setTimeout(() => setShake(0), 340);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="m-auto w-full flex flex-col items-center px-3 sm:px-6 py-8 sm:py-10">
      <div className="relative flex justify-center  items-center w-full px-2 sm:px-4 md:px-6 my-auto py-8 sm:py-12">
        {/* REGISTER CARD (animated stage: entrance / shake / exit) */}
        <AuthStage shake={shake} leaving={leaving} className="w-full max-w-3xl">
        <div className="bg-white/[.92] backdrop-blur-md border border-white/90 shadow-[0_24px_64px_-24px_rgba(23,49,81,.28)] rounded-[20px] px-6 sm:px-8 md:px-12 py-6 sm:py-8 md:py-10 w-full space-y-2">
          {/* no csf-logo animation: see Login.jsx — blend-mode logo must not
              sit inside an animated (stacking-context) wrapper */}
          <div className="flex justify-center items-center -mb-1">
            <LogoLogin />
          </div>
          <h2 className="csf-rise csf-d1 text-lg sm:text-xl md:text-2xl font-manrope text-center font-semibold text-[#173151]">
            Create Account
          </h2>
          <p className="text-center font-manrope font-normal text-xs sm:text-sm md:text-base text-[#666D80] mt-1 mb-3 sm:mb-4 md:mb-6">
            Create your account to get started.
          </p>

          <form onSubmit={handleSubmit} className="csf-rise csf-d2 flex flex-col gap-8">
            <div className="grid gap-4 md:grid-cols-2">
              {/* First Name */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="Enter your first name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.first_name ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.first_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.first_name}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Enter your last name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.last_name ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.last_name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.email ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.phone ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  placeholder="Enter your street address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.address ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.address && (
                  <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.city ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.city && (
                  <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                )}
              </div>

              {/* State */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  placeholder="Enter your state"
                  value={formData.state}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.state ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                )}
              </div>

              {/* Zip Code */}
              <div>
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Zip Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="zip_code"
                  placeholder="Enter your zip code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.zip_code ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary`}
                />
                {errors.zip_code && (
                  <p className="text-red-500 text-xs mt-1">{errors.zip_code}</p>
                )}
              </div>

              {/* Password */}
              <div className="relative">
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create a password (min 8 characters)"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.password ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 transition"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <label className="block text-xs font-manrope sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  placeholder="Confirm your password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className={`w-full font-manrope px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base rounded-md border ${errors.confirm_password ? "border-red-500" : "border-gray-300"} outline-none focus:ring-2 focus:ring-primary pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute  right-3 top-9 text-gray-500 hover:text-gray-700 transition"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
                {errors.confirm_password && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.confirm_password}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="csf-shimmer csf-press relative overflow-hidden w-full bg-primary font-['inter'] py-2 sm:py-2.5 text-sm sm:text-base rounded-lg font-semibold bg-[#F3BC48] disabled:cursor-not-allowed"
              disabled={loading}
            >
              <span
                className="transition-opacity duration-200"
                style={{ opacity: loading ? 0 : 1 }}
              >
                Register
              </span>
              <span
                aria-hidden="true"
                className={
                  "absolute left-1/2 top-1/2 -ml-2.5 -mt-2.5 h-5 w-5 rounded-full border-[2.5px] border-[#173151]/25 border-t-[#173151] transition-opacity duration-200 " +
                  (loading ? "csf-spin" : "")
                }
                style={{ opacity: loading ? 1 : 0 }}
              />
            </button>
          </form>

          <p className="csf-rise csf-d3 text-center text-sm sm:text-base text-gray-600 mt-4 sm:mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium">
              Login
            </Link>
          </p>
        </div>
        </AuthStage>
      </div>
    </div>
  );
}
